/**
 * NoticesPage — I-1. 공지사항
 *
 * - 중요 공지 배너: iconType === 'IMPORTANT', 아코디언 토글
 * - 카테고리 탭: 전체 / 서비스 업데이트 / 이용안내 / 점검공지 (클라이언트 필터)
 * - 공지 아코디언: 단건 오픈, 열릴 때 상세 조회(NoticeRow 내부) + 읽음 처리
 * - HTML 본문: dangerouslySetInnerHTML
 */
import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@indipia/ui-core';
import { fetchNotices, fetchNoticeDetail, markNoticeRead } from '../api/notices';
import type { NoticeItem } from '../api/notices';

// ── 카테고리 ────────────────────────────────────────────────────────────────

type NoticeTab = '전체' | '서비스 업데이트' | '이용안내' | '점검공지';
const TABS: NoticeTab[] = ['전체', '서비스 업데이트', '이용안내', '점검공지'];

const ICON_TYPE_TAB: Record<string, NoticeTab> = {
  UPDATE:      '서비스 업데이트',
  INFO:        '이용안내',
  EVENT:       '이용안내',
  MAINTENANCE: '점검공지',
  WARNING:     '점검공지',
};

function getTab(iconType: string | null): NoticeTab | null {
  return iconType ? (ICON_TYPE_TAB[iconType] ?? null) : null;
}

// ── 배지 스타일 ──────────────────────────────────────────────────────────────

const TAB_STYLE: Record<Exclude<NoticeTab, '전체'>, React.CSSProperties> = {
  '서비스 업데이트': {
    color: 'var(--color-info)',
    background: 'color-mix(in srgb, var(--color-info) 10%, transparent)',
    border: '1px solid color-mix(in srgb, var(--color-info) 20%, transparent)',
  },
  '이용안내': {
    color: 'var(--gt-primary)',
    background: 'color-mix(in srgb, var(--gt-primary) 10%, transparent)',
    border: '1px solid color-mix(in srgb, var(--gt-primary) 20%, transparent)',
  },
  '점검공지': {
    color: 'var(--color-warning)',
    background: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
    border: '1px solid color-mix(in srgb, var(--color-warning) 20%, transparent)',
  },
};

const TAB_ACCENT: Record<Exclude<NoticeTab, '전체'>, string> = {
  '서비스 업데이트': 'var(--color-info)',
  '이용안내': 'var(--gt-primary)',
  '점검공지': 'var(--color-warning)',
};

// ── 헬퍼 ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  return `${dt.getFullYear()}.${String(dt.getMonth() + 1).padStart(2, '0')}.${String(dt.getDate()).padStart(2, '0')}`;
}

// ── 공지 아코디언 행 ──────────────────────────────────────────────────────────

function NoticeRow({
  notice,
  isOpen,
  onToggle,
}: {
  notice: NoticeItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const tab = getTab(notice.iconType);
  const tabStyle = (tab && tab !== '전체') ? TAB_STYLE[tab] : TAB_STYLE['이용안내'];
  const accentColor = (tab && tab !== '전체') ? TAB_ACCENT[tab] : 'var(--gt-primary)';

  const { data: detail, isLoading, isError } = useQuery({
    queryKey: ['notice-detail', notice.noticeId],
    queryFn: () => fetchNoticeDetail(notice.noticeId),
    enabled: isOpen && typeof notice.noticeId === 'number',
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className="relative flex cursor-pointer items-center gap-4 px-5 py-4 transition-colors hover:bg-surface-raised"
        style={isOpen ? { background: 'color-mix(in srgb, var(--gt-primary) 3%, var(--shell-surface))' } : undefined}
      >
        {/* 좌측 액센트 바 */}
        <div
          className="absolute inset-y-0 left-0 w-0.5 rounded-r-sm transition-opacity"
          style={{ background: accentColor, opacity: isOpen ? 1 : 0 }}
        />

        {/* 카테고리 배지 */}
        {tab && (
          <Badge className="shrink-0 tracking-wide" style={tabStyle}>
            {tab}
          </Badge>
        )}

        {/* 제목 */}
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-sm font-semibold"
            style={{ color: isOpen ? 'var(--gt-primary)' : 'var(--shell-on-surface)' }}
          >
            {notice.noticeTitle}
          </p>
        </div>

        {/* 뱃지들 */}
        <div className="flex shrink-0 items-center gap-2">
          {notice.isNew && (
            <Badge
              className="font-extrabold tracking-wide"
              style={{
                color: 'var(--color-error)',
                background: 'color-mix(in srgb, var(--color-error) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--color-error) 22%, transparent)',
              }}
            >
              NEW
            </Badge>
          )}
          {notice.isRead && (
            <Badge
              style={{ color: 'var(--shell-on-surface-2)', background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
            >
              읽음
            </Badge>
          )}
        </div>

        {/* 날짜 */}
        <span className="w-20 shrink-0 text-right text-xs text-text-muted">
          {formatDate(notice.publishDtm)}
        </span>

        {/* 화살표 */}
        <svg
          className="shrink-0 transition-transform"
          style={{ opacity: 0.35, transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
          width="15" height="15" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>

      {/* 상세 본문 */}
      {isOpen && (
        <div
          className="border-b px-6 pb-5 pt-4"
          style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}
        >
          <div className="mb-3 flex items-baseline justify-between gap-4 border-b pb-3" style={{ borderColor: 'var(--shell-border)' }}>
            <p className="flex-1 text-sm font-extrabold leading-snug text-text">{notice.noticeTitle}</p>
            <span className="shrink-0 text-xs text-text-muted">
              {formatDate(notice.publishDtm)}{tab ? ` · ${tab}` : ''}
            </span>
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
              로딩 중…
            </div>
          )}

          {isError && (
            <p className="text-xs text-text-muted">내용을 불러오지 못했어요.</p>
          )}

          {!isLoading && !isError && (
            detail?.noticeContent
              ? (
                <div
                  className="notice-prose text-sm leading-relaxed"
                  style={{ color: 'var(--shell-on-surface)' }}
                  dangerouslySetInnerHTML={{ __html: detail.noticeContent }}
                />
              )
              : <p className="text-xs text-text-muted">내용이 없습니다.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── 중요 공지 배너 ────────────────────────────────────────────────────────────

function PinBanner({
  notice,
  isOpen,
  onToggle,
}: {
  notice: NoticeItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { data: detail, isLoading, isError } = useQuery({
    queryKey: ['notice-detail', notice.noticeId],
    queryFn: () => fetchNoticeDetail(notice.noticeId),
    enabled: isOpen && typeof notice.noticeId === 'number',
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="mb-6">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className="flex cursor-pointer items-center gap-4 rounded-xl border px-5 py-4 transition-all"
        style={{
          background: 'color-mix(in srgb, var(--gt-primary) 6%, var(--shell-surface))',
          borderColor: isOpen ? 'var(--gt-primary)' : 'color-mix(in srgb, var(--gt-primary) 28%, transparent)',
        }}
      >
        {/* 아이콘 */}
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
          style={{ background: 'var(--gt-primary)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </div>

        {/* 내용 */}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <Badge
              className="font-extrabold tracking-widest"
              style={{
                color: 'var(--gt-primary)',
                background: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)',
              }}
            >
              중요 공지
            </Badge>
          </div>
          <p className="truncate text-sm font-bold text-text">{notice.noticeTitle}</p>
          <p className="mt-0.5 text-xs text-text-muted">{formatDate(notice.publishDtm)} 게시</p>
        </div>

        {/* 화살표 */}
        <svg
          className="shrink-0 transition-transform"
          style={{ opacity: 0.35, transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
          width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>

      {/* 배너 상세 */}
      {isOpen && (
        <div
          className="-mt-3.5 rounded-b-xl border border-t-0 px-5 pb-5 pt-7"
          style={{
            background: 'color-mix(in srgb, var(--gt-primary) 4%, var(--shell-surface))',
            borderColor: 'color-mix(in srgb, var(--gt-primary) 22%, transparent)',
          }}
        >
          {isLoading && (
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
              로딩 중…
            </div>
          )}

          {isError && (
            <p className="text-xs text-text-muted">내용을 불러오지 못했어요.</p>
          )}

          {!isLoading && !isError && (
            detail?.noticeContent
              ? (
                <div
                  className="notice-prose text-sm leading-relaxed"
                  style={{ color: 'var(--shell-on-surface)' }}
                  dangerouslySetInnerHTML={{ __html: detail.noticeContent }}
                />
              )
              : <p className="text-xs text-text-muted">내용이 없습니다.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function NoticesPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<NoticeTab>('전체');
  const [openId, setOpenId] = useState<number | null>(null);
  const readCache = useRef<Set<number>>(new Set());

  const { data: notices = [], isLoading } = useQuery({
    queryKey: ['notices'],
    queryFn: fetchNotices,
  });

  function handleToggle(notice: NoticeItem) {
    const newId = openId === notice.noticeId ? null : notice.noticeId;
    setOpenId(newId);

    if (newId !== null && !notice.isRead && !readCache.current.has(notice.noticeId)) {
      readCache.current.add(notice.noticeId);
      queryClient.setQueryData(['notices'], (prev: NoticeItem[] | undefined) =>
        prev?.map((n) => n.noticeId === notice.noticeId ? { ...n, isRead: true } : n) ?? [],
      );
      void markNoticeRead(notice.noticeId);
    }
  }

  const pinNotice = notices.find((n) => n.iconType === 'IMPORTANT');
  const listNotices = notices.filter((n) => n.iconType !== 'IMPORTANT');

  const filteredNotices = activeTab === '전체'
    ? listNotices
    : listNotices.filter((n) => getTab(n.iconType) === activeTab);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <svg className="animate-spin text-text-muted" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
          <path d="M12 2a10 10 0 0 1 10 10" />
        </svg>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl px-20 py-8 pb-16">

        {/* 섹션 헤더 */}
        <div className="mb-7 flex items-end justify-between">
          <div>
            <p className="mb-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--gt-primary)' }}>
              공지사항
            </p>
            <h1 className="mb-1 text-xl font-extrabold tracking-tight text-text">새 소식을 전해드려요</h1>
            <p className="text-xs text-text-muted">서비스 업데이트, 이용 안내, 점검 공지</p>
          </div>
          <span className="pb-1 text-xs text-text-muted">총 {listNotices.length}건</span>
        </div>

        {/* 중요 공지 배너 */}
        {pinNotice && (
          <PinBanner
            notice={pinNotice}
            isOpen={openId === pinNotice.noticeId}
            onToggle={() => handleToggle(pinNotice)}
          />
        )}

        {/* 카테고리 탭 */}
        <div className="mb-0 flex gap-0.5 border-b" style={{ borderColor: 'var(--shell-border)' }}>
          {TABS.map((tab) => (
            <Button
              key={tab}
              variant="ghost"
              size="sm"
              onClick={() => { setActiveTab(tab); setOpenId(null); }}
              className="!rounded-none !px-4 !py-2 !text-xs !font-semibold !transition-colors hover:!bg-transparent"
              style={{
                color: activeTab === tab ? 'var(--gt-primary)' : 'var(--shell-on-surface-2)',
                borderBottom: activeTab === tab ? '2px solid var(--gt-primary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              {tab}
            </Button>
          ))}
        </div>

        {/* 공지 리스트 */}
        <div
          className="mt-5 overflow-hidden rounded-xl border"
          style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}
        >
          {filteredNotices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm text-text-muted">공지사항이 없습니다</p>
            </div>
          ) : (
            filteredNotices.map((notice, i) => (
              <div key={notice.noticeId} className={i > 0 ? 'border-t' : ''} style={{ borderColor: 'var(--shell-border)' }}>
                <NoticeRow
                  notice={notice}
                  isOpen={openId === notice.noticeId}
                  onToggle={() => handleToggle(notice)}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
